import { toast } from "react-toastify";
import axios from "./apiService";
import Swal from "sweetalert2";
import { resfresh } from "./pathApi";
import { handleGetCookie, handleSetCookie } from "../utils/commonUtil";
import { setData } from "../redux/loginSlice";
import SwalAlert from "../components/common/alert";

export const getRequest = async ({ url, params = {}, showLoading = true, headers = null }) => {
	try {
		if (showLoading) {
			Swal.fire({
				background: "transparent",
				customClass: {
					popup: "custom-loading-popup",
				},
				didOpen: () => {
					Swal.showLoading();
				},
				allowOutsideClick: false,
			});
		}
		const res = headers ? await axios.get(url, { params, headers }) : await axios.get(url, { params });

		if (res.data?.responseCode === "10402") {
			// ✅ 1) สร้าง Promise สมัครรอไว้ก่อน (กันพลาด onRefreshed)
			const waitNewToken = new Promise((resolve, reject) => {
				subscribeTokenRefresh((newToken) => {
					if (!newToken) return reject(new Error("No refreshed token"));
					resolve(newToken);
				});
			});

			// ✅ 2) ถ้ายังไม่กำลัง refresh ให้เริ่ม refresh
			if (!isRefreshing) {
				isRefreshing = true;
				refreshTokenApi()
					.then((newToken) => {
						isRefreshing = false;
						onRefreshed(newToken);
					})
					.catch((error) => {
						isRefreshing = false;
						onRefreshed(null); // ปล่อยคิวให้ reject
					});
			}

			if (showLoading) Swal.close();

			// ✅ 3) รอ token ใหม่ แล้ว retry request
			const newToken = await waitNewToken;

			return await getRequest({
				url,
				params,
				showLoading,
				headers: {
					...(headers || {}),
					Authorization: `Bearer ${newToken}`, // ✅ ใช้ newToken ตรงๆ
				},
			});
		}
		if (showLoading) {
			Swal.close();
		}
		return res.data;
	} catch (err) {
		if (showLoading) {
			Swal.close();
		}
		toast.error(err, {
			position: "top-left"
		})
		throw err
	};
};
let refreshSubscribers = [];
let isRefreshing = false; // ✅ global lock

const subscribeTokenRefresh = (cb) => {
	refreshSubscribers.push(cb);
};

const onRefreshed = (newToken) => {
	refreshSubscribers.forEach((cb) => cb(newToken));
	refreshSubscribers = [];
};

const getAccessToken = () => localStorage.getItem("accessToken");
const setAccessToken = (token) => localStorage.setItem("accessToken", token);

const refreshTokenApi = async () => {
	const authData = handleGetCookie("auth");
	const res = await axios.post(resfresh, { token: authData?.refreshToken });

	if (res?.data?.accessToken) {
		const data = {
			accessToken: res.data.accessToken,
			user: authData?.user,
			agent: authData?.agent,
			refreshToken: res.data.refreshToken,
			expiresIn: res.data.expiresIn,
		};
		handleSetCookie("auth", data, 60);
		setAccessToken(res.data.accessToken);
		return res.data.accessToken;
	}
	throw new Error("Refresh token failed");
};

export const getFileRequest = async ({
	url,
	params = {},
	showLoading = true,
	responseType = "blob",
}) => {
	try {
		if (showLoading) {
			Swal.fire({
				background: "transparent",
				customClass: {
					popup: "custom-loading-popup",
				},
				didOpen: () => {
					Swal.showLoading();
				},
				allowOutsideClick: false,
			});
		}

		const res = await axios.get(url, { params, responseType });
		return res.data;
	} catch (err) {
		toast.error(err?.message || "System Error", {
			position: "top-left",
		});
		throw err;
	} finally {
		if (showLoading) {
			Swal.close();
		}
	}
};

export const postRequest = async ({
	url,
	data = {},
	params = {},
	showLoading = true,
	showAlert = true,
	_retry = false,
	headers = null,
}) => {
	try {
		if (data?.isLoading !== false && showLoading) {
			Swal.fire({
				background: "transparent",
				customClass: { popup: "custom-loading-popup" },
				didOpen: () => Swal.showLoading(),
				allowOutsideClick: false,
			});
		}

		const res = headers
			? await axios.post(url, data, { params, headers })
			: await axios.post(url, data, { params });

		if (res === null || res?.data === undefined) {
			window.location.href = "/logout";
			return;
		}

		if (res.data?.responseCode === "10402") {
			if (_retry) {
				window.location.href = "/logout";
				return;
			}

			// ✅ 1) สร้าง Promise สมัครรอไว้ก่อน (กันพลาด onRefreshed)
			const waitNewToken = new Promise((resolve, reject) => {
				subscribeTokenRefresh((newToken) => {
					if (!newToken) return reject(new Error("No refreshed token"));
					resolve(newToken);
				});
			});

			// ✅ 2) ถ้ายังไม่กำลัง refresh ให้เริ่ม refresh
			if (!isRefreshing) {
				isRefreshing = true;
				refreshTokenApi()
					.then((newToken) => {
						isRefreshing = false;
						onRefreshed(newToken);
					})
					.catch((error) => {
						isRefreshing = false;
						onRefreshed(null); // ปล่อยคิวให้ reject
					});
			}

			if (showLoading) Swal.close();

			// ✅ 3) รอ token ใหม่ แล้ว retry request
			const newToken = await waitNewToken;

			return await postRequest({
				url,
				data,
				params,
				showLoading,
				_retry: true,
				headers: {
					...(headers || {}),
					Authorization: `Bearer ${newToken}`, // ✅ ใช้ newToken ตรงๆ
				},
			});
		}

		if (showLoading) Swal.close();

		if (res.data.responseCode !== "00000") {
			if (showAlert) {
				toast.error(res.data.responseCode + " " + res.data.responseDescription, {
					position: "top-left",
				});
			}
		}

		return res.data;
	} catch (err) {
		if (showLoading) Swal.close();

		if (err?.response?.status === 401) {
			window.location.href = "/logout";
		}

		toast.error(err?.message || "System Error", { position: "top-left" });
		throw err;
	}
};

export const postFormDataRequest = async ({
	url,
	formData,
	showLoading = true,
	headers = null,
	_retry = false,
}) => {
	try {
		if (showLoading) {
			Swal.fire({
				background: "transparent",
				customClass: {
					popup: "custom-loading-popup",
				},
				didOpen: () => {
					Swal.showLoading();
				},
				allowOutsideClick: false,
			});
		}

		const config = headers ? { headers } : {};
		const res = await axios.post(url, formData, config);

		if (res.data?.responseCode === "10402") {
			if (_retry) {
				window.location.href = "/logout";
				return;
			}

			const waitNewToken = new Promise((resolve, reject) => {
				subscribeTokenRefresh((newToken) => {
					if (!newToken) {
						reject(new Error("No refreshed token"));
						return;
					}

					resolve(newToken);
				});
			});

			if (!isRefreshing) {
				isRefreshing = true;

				refreshTokenApi()
					.then((newToken) => {
						isRefreshing = false;
						onRefreshed(newToken);
					})
					.catch(() => {
						isRefreshing = false;
						onRefreshed(null);
					});
			}

			if (showLoading) {
				Swal.close();
			}

			const newToken = await waitNewToken;

			return await postFormDataRequest({
				url,
				formData,
				showLoading,
				headers: {
					...(headers || {}),
					Authorization: `Bearer ${newToken}`,
				},
				_retry: true,
			});
		}

		if (showLoading) {
			Swal.close();
		}

		return res.data;
	} catch (err) {
		if (showLoading) {
			Swal.close();
		}

		toast.error(err?.message || "System Error", {
			position: "top-left",
		});
		throw err;
	}
};

export const patchRequest = async ({ url, data = {}, params = {} }) => {
	try {
		const res = await axios.patch(url, data, { params });
		return res.data;
	} catch (err) {
		throw err
	};
};

export const patchFormDataRequest = async ({ url, data = {}, params = {} }) => {
	try {
		const res = await axios.patch(url, data, {
			params,
			headers: {
				"Content-Type": "multipart/form-data"
			},
		});
		return res.data;
	} catch (err) {
		toast.error(err, {
			position: "top-left"
		})
		throw err
	};
};

export const putRequest = async ({ url, data = {}, params = {}, showLoading = true }) => {
	try {
		if (showLoading) {
			Swal.fire({
				background: "transparent",
				customClass: {
					popup: "custom-loading-popup",
				},
				didOpen: () => {
					Swal.showLoading();
				},
			});
		}
		const res = await axios.put(url, data, { params });
		if (showLoading) {
			Swal.close();
		}
		return res.data;
	} catch (err) {
		if (showLoading) {
			Swal.close();
		}
		toast.error(err, {
			position: "top-left"
		})
		throw err
	};
};

export const deleteRequest = async ({ url, params = {}, showLoading = true }) => {
	try {
		if (showLoading) {
			Swal.fire({
				background: "transparent",
				customClass: {
					popup: "custom-loading-popup",
				},
				didOpen: () => {
					Swal.showLoading();
				},
				allowOutsideClick: false,
			});
		}
		const res = await axios.delete(url, { params });
		if (showLoading) {
			Swal.close();
		}
		return res.data;
	} catch (err) {
		if (showLoading) {
			Swal.close();
		}

		return { responseCode: "9999", responseDescription: "Error from api!" }
	};
};


export const postExport = async ({ url, data = {}, params = {}, showLoading = true, responseType = "json" }) => {
	try {
		if (showLoading) {
			Swal.fire({
				background: "transparent",
				customClass: { popup: "custom-loading-popup" },
				didOpen: () => Swal.showLoading(),
				allowOutsideClick: false,
			});
		}
		const res = await axios.post(url, data, { params, responseType });
		if (res?.data?.responseCode === "10402") {
			window.location.href = "/logout";
			return null;
		}
		return res.data;
	} catch (err) {
		toast.error(err, {
			position: "top-left"
		})
		throw err;
	} finally {
		if (showLoading) Swal.close();
	}
};